/** B3 — record a manual Individual Insolvency Register search: result, reference, when, and the REQUIRED screenshot / PDF
 *  evidence (chunked upload → OneDrive "Register searches"). Inline under the gate item that needs it. */
import { useRef, useState } from "react";
import { ExternalLink, Loader2, Paperclip, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { legalUploadChunk, recordRegisterCheck, uploadInboxAttachment } from "@/lib/api";

const nowLocal = () => { const d = new Date(); d.setSeconds(0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const errMsg = (e) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || e.message; };

export function RegisterCheckEvidence({ check }) {
  if (!check?.evidence?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="register-check-evidence">
      {check.evidence.map((f, i) => (
        <a key={`${f.file_id || f.name}-${i}`} href={f.web_url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] border rounded bg-white px-1.5 py-0.5 text-indigo-700 hover:underline max-w-[220px]" data-testid={`register-check-evidence-${i}`}>
          <ExternalLink className="w-3 h-3 shrink-0" /><span className="truncate">{f.name}</span>
        </a>
      ))}
    </div>
  );
}

export default function RegisterCheckForm({ caseId, onDone }) {
  const [form, setForm] = useState({ result: "clear", reference: "", searched_at: nowLocal(), notes: "" });
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const addFiles = (list) => setFiles((cur) => [...cur, ...Array.from(list || []).filter((f) => !cur.some((x) => x.name === f.name && x.size === f.size))]);
  const valid = files.length > 0 && !!form.searched_at;

  const save = async () => {
    setBusy(true);
    try {
      const evidence = [];
      for (let i = 0; i < files.length; i++) {
        setProgress({ name: files[i].name, i: i + 1, n: files.length, pct: 0 });
        evidence.push(await uploadInboxAttachment(legalUploadChunk, files[i], (pct) => setProgress((p) => ({ ...p, pct }))));
      }
      setProgress(null);
      const r = await recordRegisterCheck(caseId, { ...form, searched_at: new Date(form.searched_at).toISOString(), evidence });
      (r.record?.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Register search recorded · ${evidence.length} evidence file${evidence.length === 1 ? "" : "s"} saved to OneDrive`);
      onDone(r);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setProgress(null); }
  };

  return (
    <div className="mt-1.5 border rounded p-2 bg-white space-y-1.5" data-testid="register-check-form" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Select value={form.result} onValueChange={set("result")}>
          <SelectTrigger className="h-7 text-[11px] w-40" data-testid="register-check-result"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="clear" className="text-xs">No entries found</SelectItem>
            <SelectItem value="entries_found" className="text-xs">Entries found</SelectItem>
          </SelectContent>
        </Select>
        <Input value={form.reference} onChange={set("reference")} placeholder="Search reference" className="h-7 text-[11px] w-36" data-testid="register-check-reference" />
        <Input type="datetime-local" value={form.searched_at} max={nowLocal()} onChange={set("searched_at")} className="h-7 text-[11px] w-44" title="When the register was searched" data-testid="register-check-searched-at" />
        <Input value={form.notes} onChange={set("notes")} placeholder="Note" className="h-7 text-[11px] flex-1 min-w-[120px]" data-testid="register-check-notes" />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="register-check-attach-btn">
          <Paperclip className="w-3 h-3 mr-1" />Add evidence
        </Button>
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="register-check-file-input" />
        {files.length === 0 ? <span className="text-[10px] text-rose-700" data-testid="register-check-evidence-required">Screenshot or PDF of the IIR search is required — saved to the file's OneDrive folder</span>
          : files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 border rounded bg-slate-50 px-2 py-0.5 text-[11px] max-w-[220px]" data-testid={`register-check-file-${i}`}>
              <span className="truncate">{f.name}</span><span className="text-slate-400">{fmtSize(f.size)}</span>
              <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setFiles(files.filter((_, j) => j !== i))} disabled={busy} data-testid={`register-check-file-remove-${i}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        <Button size="sm" className="h-7 text-[11px] ml-auto" onClick={save} disabled={busy || !valid} data-testid="register-check-save">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}{busy ? (progress ? `Uploading ${progress.pct}%` : "Saving…") : "Record search"}
        </Button>
      </div>
    </div>
  );
}
